import { LocalizedTextMap } from '@dixa/messenger-api-payloads';
import { ElevioMessengerDisplayType } from '@dixa/messenger-api-payloads/dist/lib/settings/v2';
import React, { useCallback, useRef, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { BulletinMeta } from 'apis/bulletinboards';
import { KnowledgeState } from 'app/views/layout/views/settings/views/messenger/views/messengerSettings/messengerSettings.types';

import { MessengerTranslatedLanguage } from '../components/generalSettings/languageSettings/languages';
import { FormValues } from '../types';
import { resolveLogoUrl } from '../utils';

type MessageTypes =
  | 'setBgColor'
  | 'setLocalizedTitle'
  | 'setLocalizedDescription'
  | 'setLocalizedStartConversation'
  | 'setLocalizedFindArticles'
  | 'setBulletinActive'
  | 'setBulletinList'
  | 'setLanguage'
  | 'setAvatars'
  | 'setLogo'
  | 'setElevioIntegration'
  | 'setMessengerMode';

export type SetPreviewLanguageFn = (
  language: MessengerTranslatedLanguage,
) => void;

interface ReturnValue {
  loaded: boolean;
  ref: React.RefObject<HTMLIFrameElement>;
  onLoad: () => void;
  setPreviewLanguage: SetPreviewLanguageFn;
}

export function useMessengerPreview(
  {
    bulletinBoardEnabled,
    title,
    description,
    startConversation,
    findArticles,
    backgroundColor,
    logo,
    messengerMode,
  }: Partial<FormValues> = {},
  bulletins: ReadonlyArray<BulletinMeta>,
  avatars: string[],
  knowledgeState: KnowledgeState,
): ReturnValue {
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const previewIframeRef = useRef<HTMLIFrameElement | null>(null);

  const sendFormValuesOverToPreview = useCallback(
    (payload: { type: MessageTypes; payload: unknown }) => {
      const timeoutId = setTimeout(() => {
        previewIframeRef.current?.contentWindow?.postMessage(payload, '*');
      });
      return () => clearTimeout(timeoutId);
    },
    [],
  );

  const updateBgColor = useCallback(
    (color?: FormValues['backgroundColor']) => {
      if (color !== undefined) {
        sendFormValuesOverToPreview({ type: 'setBgColor', payload: color });
      }
    },
    [sendFormValuesOverToPreview],
  );

  const updateLocalizedTitle = useCallback(
    (title?: LocalizedTextMap) => {
      if (title !== undefined) {
        sendFormValuesOverToPreview({
          type: 'setLocalizedTitle',
          payload: title,
        });
      }
    },
    [sendFormValuesOverToPreview],
  );

  const updateLocalizedDescription = useCallback(
    (description?: LocalizedTextMap) => {
      if (description !== undefined) {
        sendFormValuesOverToPreview({
          type: 'setLocalizedDescription',
          payload: description,
        });
      }
    },
    [sendFormValuesOverToPreview],
  );

  const updateLocalizedStartConversation = useCallback(
    (startConversation?: LocalizedTextMap) => {
      if (startConversation !== undefined) {
        sendFormValuesOverToPreview({
          type: 'setLocalizedStartConversation',
          payload: startConversation,
        });
      }
    },
    [sendFormValuesOverToPreview],
  );

  const updateLocalizedFindArticles = useCallback(
    (findArticles?: LocalizedTextMap) => {
      if (findArticles !== undefined) {
        sendFormValuesOverToPreview({
          type: 'setLocalizedFindArticles',
          payload: findArticles,
        });
      }
    },
    [sendFormValuesOverToPreview],
  );

  const updateBulletinActive = useCallback(
    (bulletinBoardEnabled?: boolean) => {
      if (bulletinBoardEnabled !== undefined) {
        sendFormValuesOverToPreview({
          type: 'setBulletinActive',
          payload: bulletinBoardEnabled,
        });
      }
    },
    [sendFormValuesOverToPreview],
  );

  const updateBulletinList = useCallback(
    (bulletins?: ReadonlyArray<BulletinMeta>) => {
      if (bulletins !== undefined) {
        sendFormValuesOverToPreview({
          type: 'setBulletinList',
          payload: bulletins.filter(({ active }) => active),
        });
      }
    },
    [sendFormValuesOverToPreview],
  );

  const setPreviewLanguage = useCallback(
    (language: MessengerTranslatedLanguage) => {
      sendFormValuesOverToPreview({
        type: 'setLanguage',
        payload: language,
      });

      previewIframeRef.current?.setAttribute(
        'dir',
        isRtlLanguage(language) ? 'rtl' : 'ltr',
      );
    },
    [sendFormValuesOverToPreview],
  );

  const updateAvatars = useCallback(
    (avatars: string[]) => {
      sendFormValuesOverToPreview({
        type: 'setAvatars',
        payload: avatars,
      });
    },
    [sendFormValuesOverToPreview],
  );

  const updateLogo = useCallback(
    (logo?: string) => {
      sendFormValuesOverToPreview({
        type: 'setLogo',
        payload: resolveLogoUrl(logo),
      });
    },
    [sendFormValuesOverToPreview],
  );

  const updateElevioIntegration = useCallback(
    (payload?: {
      displayType: ElevioMessengerDisplayType;
      searchBarPlaceholder: LocalizedTextMap;
    }) => {
      sendFormValuesOverToPreview({
        type: 'setElevioIntegration',
        payload,
      });
    },
    [sendFormValuesOverToPreview],
  );

  const updateElevioIntegrationKnowledgeFirst = useCallback(
    (knowledgeState: KnowledgeState) => {
      const { elevioIntegration, selectedLanguage } = knowledgeState;
      if (elevioIntegration.type === 'connected') {
        const { enabled, displayType, translations } = elevioIntegration;
        const { findArticles, searchBarPlaceholder } = translations;
        if (enabled) {
          setPreviewLanguage(selectedLanguage);
          updateElevioIntegration({
            displayType,
            searchBarPlaceholder: searchBarPlaceholder.translatedMap,
          });
          updateLocalizedFindArticles(findArticles.translatedMap);
        } else {
          updateElevioIntegration();
        }
      } else {
        updateElevioIntegration();
      }
    },
    [setPreviewLanguage, updateElevioIntegration, updateLocalizedFindArticles],
  );

  const updateMessengerInitialMode = useCallback(
    (mode: FormValues['messengerMode'] | undefined) => {
      if (!mode) {
        return;
      }

      sendFormValuesOverToPreview({
        type: 'setMessengerMode',
        payload: mode,
      });
    },
    [sendFormValuesOverToPreview],
  );

  useUpdateEffect(
    () => updateMessengerInitialMode(messengerMode),
    [messengerMode],
  );
  useUpdateEffect(() => updateBgColor(backgroundColor), [backgroundColor]);
  useUpdateEffect(() => updateLocalizedTitle(title), [title]);
  useUpdateEffect(() => updateLocalizedDescription(description), [description]);
  useUpdateEffect(
    () => updateLocalizedStartConversation(startConversation),
    [startConversation],
  );
  useUpdateEffect(
    () => updateLocalizedFindArticles(findArticles),
    [findArticles],
  );
  useUpdateEffect(
    () => updateBulletinActive(bulletinBoardEnabled),
    [bulletinBoardEnabled],
  );
  useUpdateEffect(() => updateBulletinList(bulletins), [bulletins]);
  useUpdateEffect(() => updateLogo(logo), [logo]);

  useUpdateEffect(
    () => updateElevioIntegrationKnowledgeFirst(knowledgeState),
    [knowledgeState],
  );

  const handlePreviewLoaded = useCallback(() => {
    // first set the initial values so that the widget loads with all the correct customizations,
    // before it is presented to the user
    updateBgColor(backgroundColor);
    updateLocalizedTitle(title);
    updateLocalizedDescription(description);
    updateLocalizedStartConversation(startConversation);
    updateLocalizedFindArticles(findArticles);
    updateBulletinActive(bulletinBoardEnabled);
    updateBulletinList(bulletins);
    updateAvatars(avatars);
    updateLogo(logo);
    updateElevioIntegrationKnowledgeFirst(knowledgeState);

    // Then end the loading
    setTimeout(() => {
      setPreviewLoaded(true);
    });
  }, [
    backgroundColor,
    title,
    description,
    startConversation,
    findArticles,
    bulletinBoardEnabled,
    bulletins,
    avatars,
    logo,
    updateBgColor,
    updateLocalizedTitle,
    updateLocalizedDescription,
    updateLocalizedStartConversation,
    updateLocalizedFindArticles,
    updateBulletinActive,
    updateBulletinList,
    updateAvatars,
    updateLogo,
    knowledgeState,
    updateElevioIntegrationKnowledgeFirst,
  ]);

  return {
    loaded: previewLoaded,
    ref: previewIframeRef,
    onLoad: handlePreviewLoaded,
    setPreviewLanguage,
  };
}

function isRtlLanguage(languageCode: MessengerTranslatedLanguage) {
  const rtlLanguages: MessengerTranslatedLanguage[] = ['ar'];

  return rtlLanguages.includes(languageCode);
}
